<template>
  <div class="es-root" :class="{ 'is-open': open, 'is-disabled': disabled }">
    <div class="es-toggle" @mousedown.prevent="toggle" ref="toggle">
      <span v-if="displayLabel" class="es-value">{{ displayLabel }}</span>
      <span v-else class="es-placeholder">{{ placeholder }}</span>
      <div class="es-actions">
        <button
          v-if="clearable && currentValue != null"
          class="es-clear"
          @mousedown.stop="clear"
          tabindex="-1"
        >
          <i class="fa fa-times"></i>
        </button>
        <span v-if="loading && !open" class="es-spinner"></span>
        <i v-else class="fa fa-chevron-down es-arrow"></i>
      </div>
    </div>

    <portal to="editor-select-portal">
      <transition name="es-drop">
        <div
          v-if="open"
          class="es-dropdown-portal"
          :style="dropdownStyle"
          @mousedown.stop
        >
          <div class="es-dropdown">
            <div class="es-search-wrap">
              <input
                ref="search"
                v-model="query"
                class="es-search"
                placeholder="Search…"
                @keydown.esc="close"
                @keydown.down.prevent="moveHighlight(1)"
                @keydown.up.prevent="moveHighlight(-1)"
                @keydown.enter.prevent="selectHighlighted"
              />
            </div>

            <div class="es-options" ref="options" @scroll="onScroll">
              <div
                v-for="(opt, idx) in items"
                :key="getOptionKey(opt, idx)"
                class="es-option"
                :class="{
                  'is-highlighted': idx === highlightedIndex,
                  'is-selected': isSelected(opt),
                }"
                @mouseenter="highlightedIndex = idx"
                @click.prevent="selectOption(opt)"
              >
                {{ getOptionLabel(opt) }}
              </div>
              <div v-if="items.length === 0 && !loading" class="es-no-options">
                No options
              </div>
              <div v-if="loading && items.length === 0" class="es-loading">
                Loading…
              </div>
              <div v-if="hasNextPage" class="es-load-more">
                <span v-if="loadingMore" class="es-spinner es-spinner--small"></span>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </portal>
  </div>
</template>

<script>
import _ from "lodash";

export default {
  name: "EditorSelectAsync",
  props: {
    value: { default: null },
    onFetch: { type: Function, required: true },
    label: { type: String, default: "label" },
    placeholder: { type: String, default: "Select…" },
    clearable: { type: Boolean, default: true },
    disabled: { type: Boolean, default: false },
  },
  data() {
    return {
      open: false,
      query: "",
      items: [],
      page: 1,
      totalPages: 1,
      loading: false,
      loadingMore: false,
      highlightedIndex: 0,
      dropdownStyle: {},
    };
  },
  computed: {
    currentValue() {
      return this.value;
    },
    hasNextPage() {
      return this.page < this.totalPages;
    },
    displayLabel() {
      if (this.currentValue == null) return "";
      if (typeof this.currentValue === "string") return this.currentValue;
      if (this.label && this.currentValue[this.label] != null)
        return String(this.currentValue[this.label]);
      return String(this.currentValue);
    },
  },
  methods: {
    getOptionLabel(opt) {
      if (opt == null) return "";
      if (typeof opt === "string" || typeof opt === "number") return String(opt);
      if (this.label && opt[this.label] != null) return String(opt[this.label]);
      return String(opt);
    },
    getOptionKey(opt, idx) {
      if (this.label && opt && opt[this.label] != null) return `${opt[this.label]}_${idx}`;
      return idx;
    },
    isSelected(opt) {
      if (this.currentValue == null) return false;
      if (this.label && opt && this.currentValue) {
        return opt[this.label] === this.currentValue[this.label];
      }
      return opt === this.currentValue;
    },
    toggle() {
      if (this.disabled) return;
      this.open ? this.close() : this.openDropdown();
    },
    openDropdown() {
      this.open = true;
      this.query = "";
      this.highlightedIndex = 0;
      this.updatePosition();
      this.$nextTick(() => {
        if (this.$refs.search) this.$refs.search.focus();
      });
      this._onDocClick = (e) => {
        if (!this.$el.contains(e.target)) this.close();
      };
      this._onScroll = () => this.updatePosition();
      setTimeout(() => {
        document.addEventListener("mousedown", this._onDocClick);
        window.addEventListener("scroll", this._onScroll, true);
        window.addEventListener("resize", this._onScroll);
      }, 0);
    },
    close() {
      if (!this.open) return;
      this.open = false;
      this.query = "";
      this.cleanupListeners();
    },
    cleanupListeners() {
      if (this._onDocClick) {
        document.removeEventListener("mousedown", this._onDocClick);
        this._onDocClick = null;
      }
      if (this._onScroll) {
        window.removeEventListener("scroll", this._onScroll, true);
        window.removeEventListener("resize", this._onScroll);
        this._onScroll = null;
      }
    },
    updatePosition() {
      const toggle = this.$refs.toggle;
      if (!toggle) return;
      const rect = toggle.getBoundingClientRect();
      this.dropdownStyle = {
        position: "fixed",
        top: rect.bottom + "px",
        left: rect.left + "px",
        width: rect.width + "px",
        zIndex: 999999,
      };
    },
    selectOption(opt) {
      this.$emit("input", opt);
      this.close();
    },
    clear() {
      this.$emit("input", null);
    },
    moveHighlight(dir) {
      const len = this.items.length;
      if (!len) return;
      this.highlightedIndex = (this.highlightedIndex + dir + len) % len;
      this.scrollToHighlighted();
    },
    selectHighlighted() {
      if (this.items[this.highlightedIndex]) {
        this.selectOption(this.items[this.highlightedIndex]);
      }
    },
    scrollToHighlighted() {
      this.$nextTick(() => {
        const container = this.$refs.options;
        if (!container) return;
        const el = container.children[this.highlightedIndex];
        if (el) el.scrollIntoView({ block: "nearest" });
      });
    },
    async fetchItems(query, resetPage) {
      if (resetPage) {
        this.page = 1;
        this.items = [];
        this.loading = true;
      } else {
        this.loadingMore = true;
      }
      try {
        const { items = [], pages = 0 } = await this.onFetch(query || "", this.page);
        if (resetPage) {
          this.items = items;
        } else {
          this.items = this.items.concat(items);
        }
        this.totalPages = pages;
      } catch (err) {
        this.$emit("onError", err);
      } finally {
        this.loading = false;
        this.loadingMore = false;
      }
    },
    async loadNextPage() {
      if (!this.hasNextPage || this.loadingMore) return;
      this.page += 1;
      await this.fetchItems(this.query, false);
    },
    onScroll() {
      const el = this.$refs.options;
      if (!el) return;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) {
        this.loadNextPage();
      }
    },
  },
  watch: {
    query() {
      this.debouncedSearch(this.query);
    },
  },
  created() {
    this.debouncedSearch = _.debounce((q) => this.fetchItems(q, true), 400);
  },
  mounted() {
    this.fetchItems("", true);
  },
  beforeDestroy() {
    this.cleanupListeners();
    if (this.debouncedSearch && this.debouncedSearch.cancel) this.debouncedSearch.cancel();
  },
};
</script>

<style scoped lang="scss">
$bg-toggle:      #1e2130;
$border:         #3a3f55;
$border-focus:   #5b7cf6;
$text-primary:   #e8eaf2;
$text-muted:     #6b7394;
$text-placeholder: #555d7a;
$accent:         #5b7cf6;
$radius:         6px;
$danger:         #f87171;

.es-root {
  position: relative;
  width: 100%;
  font-size: 13px;
}

.es-toggle {
  display: flex;
  align-items: center;
  min-height: 36px;
  padding: 0 8px 0 12px;
  background: $bg-toggle;
  border: 1px solid $border;
  border-radius: $radius;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;
  gap: 8px;

  .is-open & {
    border-color: $border-focus;
    box-shadow: 0 0 0 2px rgba($border-focus, 0.15);
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  .is-disabled & {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.es-value {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: $text-primary;
}

.es-placeholder {
  flex: 1;
  color: $text-placeholder;
}

.es-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.es-clear {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  border-radius: 3px;
  background: transparent;
  color: $text-muted;
  font-size: 10px;
  cursor: pointer;
  transition: color 0.12s, background 0.12s;

  &:hover { color: $danger; background: rgba($danger, 0.1); }
}

.es-arrow {
  font-size: 10px;
  color: $text-muted;
  transition: transform 0.18s ease;

  .is-open & { transform: rotate(180deg); }
}

.es-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba($text-muted, 0.2);
  border-left-color: $accent;
  border-radius: 50%;
  animation: es-spin 0.8s linear infinite;
  display: inline-block;
}

@keyframes es-spin {
  to { transform: rotate(360deg); }
}
</style>
